import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { joiResolver } from "@hookform/resolvers/joi";
import LOGO from "assets/logo.svg";
import Joi from "joi";
import { Loader2Icon, MailIcon } from "lucide-react";
import { getApiErrorMessage } from "utils/apiErrorMessage";
import { toast } from "utils/toast";

import { useDecodeInviteToken } from "hooks/useDecodeInviteToken";

import { Button } from "js/components/ui/button";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import { PasswordInput } from "js/components/ui/password-input";

import { userLogin } from "../../../store/features/auth/authActions";

interface LoginFormData {
  email: string;
  password: string;
}

const Login = () => {
  const navigate = useNavigate();
  const onLoginSuccess = () => navigate("/");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const schema = Joi.object({
    email: Joi.string()
      .email({
        minDomainSegments: 2,
        tlds: { allow: false },
      })
      .required()
      .messages({
        "string.email": "Ingresa un correo electronico valido",
        "string.empty": "El correo es requerido",
        "any.required": "El correo es requerido",
      }),
    password: Joi.string().required().messages({
      "string.empty": "La contrasena es requerida",
      "any.required": "La contrasena es requerida",
    }),
  });

  const { decodedInviteJWT } = useDecodeInviteToken();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: joiResolver(schema),
    defaultValues: {
      email: decodedInviteJWT.email || "",
      password: "",
    },
  });

  useEffect(() => {
    if (decodedInviteJWT.email) {
      setValue("email", decodedInviteJWT.email);
    }
  }, [decodedInviteJWT, setValue]);

  const onSubmit = (data: LoginFormData) => {
    const lowercaseData = {
      ...data,
      email: data.email.toLowerCase(),
    };
    setLoading(true);
    dispatch(userLogin(lowercaseData) as any)
      .unwrap()
      .then(() => {
        onLoginSuccess();
      })
      .catch((error: unknown) => {
        // El tipo `string` de antes era falso: con `.unwrap()` tambien puede
        // llegar un SerializedError o el diccionario de errores de la API.
        toast.error(
          getApiErrorMessage(
            error,
            "No pudimos iniciar sesión. Inténtalo de nuevo.",
          ),
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="space-y-8">
      {/* Logo - visible only on mobile */}
      <div className="flex justify-start lg:hidden">
        <a href="https://multiburo.com.mx/">
          <img src={LOGO} alt="Multiburo" className="h-14 w-auto" />
        </a>
      </div>

      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          ¡Hola, Bienvenido!
        </h1>
        <p className="text-muted-foreground">
          Ingresa tus credenciales para acceder a tu cuenta
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email">Correo Electrónico</Label>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <div className="relative">
                <MailIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@correo.com"
                  className={`h-11 pl-10 ${errors.email ? "border-destructive" : ""}`}
                  data-sentry-ignore
                  {...field}
                />
              </div>
            )}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Contraseña</Label>
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-sm font-normal"
              onClick={() => navigate("/forgot-password")}
            >
              ¿Olvidaste tu contraseña?
            </Button>
          </div>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <PasswordInput
                id="password"
                placeholder="********"
                className={`h-11 ${errors.password ? "border-destructive" : ""}`}
                data-sentry-ignore
                {...field}
              />
            )}
          />
          {errors.password && (
            <p className="text-sm text-destructive">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className="h-11 w-full text-base font-semibold"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              Iniciando sesión...
            </>
          ) : (
            "Iniciar Sesión"
          )}
        </Button>
      </form>

      {/* Sign Up Link */}
      <p className="text-center text-sm text-muted-foreground">
        ¿Aún no tienes una cuenta?{" "}
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 font-semibold"
          onClick={() => navigate("/signup")}
        >
          Regístrate
        </Button>
      </p>
    </div>
  );
};

export default Login;
